/**
 * Helpers for cart lines: unique line identifiers and combined stock accounting
 * across several lines of the same product.
 */

/** Minimal line shape needed for stock accounting. */
export interface StockLine {
  lineId: string;
  productId: string;
  quantity: number;
  availableStock: number;
}

let lineIdCounter = 0;

const HEX = '0123456789abcdef';

/**
 * Builds a RFC 4122 version 4 style identifier from a random byte source.
 *
 * @param bytes - 16 random bytes
 * @returns UUID formatted string
 */
const formatUuidV4 = (bytes: ArrayLike<number>): string => {
  const b = Array.from({ length: 16 }, (_, i) => bytes[i] & 0xff);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const hex = b.map((v) => HEX[v >> 4] + HEX[v & 0x0f]).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * Generates a unique identifier for a cart line.
 * Uses `crypto.randomUUID()` when available (secure contexts), then
 * `crypto.getRandomValues()`, and finally a time/counter/random based fallback
 * so it also works over plain HTTP on a LAN.
 *
 * @returns A unique string identifier
 */
export const generateLineId = (): string => {
  const cryptoRef: Crypto | undefined = typeof crypto !== 'undefined' ? crypto : undefined;

  if (cryptoRef && typeof cryptoRef.randomUUID === 'function') {
    try {
      return cryptoRef.randomUUID();
    } catch {
      // fall through to the next strategy
    }
  }

  if (cryptoRef && typeof cryptoRef.getRandomValues === 'function') {
    try {
      return formatUuidV4(cryptoRef.getRandomValues(new Uint8Array(16)));
    } catch {
      // fall through to the next strategy
    }
  }

  lineIdCounter = (lineIdCounter + 1) % Number.MAX_SAFE_INTEGER;
  const random = Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));
  const stamp = Date.now();
  random[0] = (stamp >>> 0) & 0xff;
  random[1] = (stamp >>> 8) & 0xff;
  random[2] = lineIdCounter & 0xff;
  random[3] = (lineIdCounter >>> 8) & 0xff;
  return formatUuidV4(random);
};

/**
 * Fraction (0..n) of the product's total stock consumed by its cart lines.
 * Each line carries `availableStock` expressed in its own selling unit, so
 * `quantity / availableStock` is unit-independent: 1 means all stock is used.
 *
 * @param items - Cart lines
 * @param productId - Product to account for
 * @param excludeLineId - Optional line to leave out of the sum
 * @returns Consumed stock fraction
 */
export const getStockUsageFraction = (
  items: ReadonlyArray<StockLine>,
  productId: string,
  excludeLineId?: string
): number =>
  items.reduce((sum, line) => {
    if (line.productId !== productId || line.lineId === excludeLineId) return sum;
    if (!Number.isFinite(line.availableStock) || line.availableStock <= 0) return sum;
    return sum + line.quantity / line.availableStock;
  }, 0);

/**
 * Quantity (in the unit described by `availableStock`) that can still be added
 * for a product after the other cart lines have taken their share of stock.
 *
 * @param items - Cart lines
 * @param productId - Product to check
 * @param availableStock - Total stock expressed in the unit of the line being sized
 * @param excludeLineId - Line whose own quantity should not count as consumed
 * @returns Remaining quantity rounded down to hundredths (never negative)
 */
export const getRemainingQuantity = (
  items: ReadonlyArray<StockLine>,
  productId: string,
  availableStock: number,
  excludeLineId?: string
): number => {
  if (!Number.isFinite(availableStock) || availableStock <= 0) return 0;
  const usage = getStockUsageFraction(items, productId, excludeLineId);
  const remaining = Math.max(0, 1 - usage) * availableStock;
  return Math.floor((remaining + 1e-6) * 100) / 100;
};
